import { resolveUploadPortPath } from '@/features/upload/resolveUploadPort';
import {
  connectSerial,
  disconnectSerial,
  addAuthorizedWebSerialDevice,
  listAvailablePorts,
  type SerialPortOption,
  writeSerial,
} from '@/features/serial/serialManager';
import { markHelperInstalledOnThisDevice } from '@/config/helperPresence';
import { fetchHelperHealth, resolveHelperConnectionState } from '@/features/serial/helperClient';
import { createSerialLineBuffer, type SerialLineBuffer } from '@/features/serial/serialLineBuffer';
import {
  isWebSerialSupported,
  setUnexpectedDisconnectHandler,
} from '@/features/serial/webSerialTransport';
import { useConsoleStore } from '@/state/consoleStore';
import type { SerialLineEnding } from '@skyrover/shared/constants';
import { SERIAL_DEFAULT_BAUD } from '@skyrover/shared/constants';
import { create } from 'zustand';

export interface SerialLogEntry {
  id: string;
  timestamp: Date;
  direction: 'rx' | 'tx' | 'system';
  text: string;
}

interface SerialState {
  connected: boolean;
  connecting: boolean;
  pausedForUpload: boolean;
  portsLoading: boolean;
  helperOnline: boolean;
  helperSetupRequired: boolean;
  webSerialSupported: boolean;
  port: string;
  baudRate: number;
  autoScroll: boolean;
  showTimestamp: boolean;
  lineEnding: SerialLineEnding;
  inputText: string;
  logs: SerialLogEntry[];
  availablePorts: SerialPortOption[];
  initialize: () => void;
  setPort: (port: string) => void;
  setBaudRate: (baud: number) => void;
  setAutoScroll: (value: boolean) => void;
  setShowTimestamp: (value: boolean) => void;
  setLineEnding: (value: SerialLineEnding) => void;
  setInputText: (text: string) => void;
  connect: () => Promise<void>;
  disconnect: (options?: { unexpected?: boolean; forUpload?: boolean }) => Promise<void>;
  send: () => Promise<void>;
  clear: () => void;
  refreshPorts: (options?: { silent?: boolean }) => Promise<void>;
  refreshHelperStatus: () => Promise<void>;
  addWebSerialDevice: () => Promise<void>;
  getSelectedPortLabel: () => string;
}

function appendLog(
  logs: SerialLogEntry[],
  direction: SerialLogEntry['direction'],
  text: string,
): SerialLogEntry[] {
  return [
    ...logs,
    {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      timestamp: new Date(),
      direction,
      text,
    },
  ];
}

function lineEndingSuffix(lineEnding: SerialLineEnding): string {
  if (lineEnding === 'nl') {
    return '\n';
  }
  if (lineEnding === 'cr') {
    return '\r';
  }
  if (lineEnding === 'crlf') {
    return '\r\n';
  }
  return '';
}

let activeRxLineBuffer: SerialLineBuffer | null = null;

function flushRxLineBuffer(): void {
  activeRxLineBuffer?.flush();
  activeRxLineBuffer = null;
}

export const useSerialStore = create<SerialState>((set, get) => ({
  connected: false,
  connecting: false,
  pausedForUpload: false,
  portsLoading: false,
  helperOnline: false,
  helperSetupRequired: false,
  webSerialSupported: false,
  port: '',
  baudRate: SERIAL_DEFAULT_BAUD,
  autoScroll: true,
  showTimestamp: true,
  lineEnding: 'nl',
  inputText: '',
  logs: [],
  availablePorts: [],

  initialize: () => {
    set({ webSerialSupported: isWebSerialSupported() });
    setUnexpectedDisconnectHandler(() => {
      void get().disconnect({ unexpected: true });
    });
    void get().refreshPorts();
  },

  getSelectedPortLabel: () => {
    const { port, availablePorts } = get();
    return availablePorts.find((entry) => entry.id === port)?.label ?? port;
  },

  setPort: (port) => {
    if (get().connected) {
      void get().disconnect();
    }
    set({ port });
  },

  setBaudRate: (baudRate) => set({ baudRate }),
  setAutoScroll: (autoScroll) => set({ autoScroll }),
  setShowTimestamp: (showTimestamp) => set({ showTimestamp }),
  setLineEnding: (lineEnding) => set({ lineEnding }),
  setInputText: (inputText) => set({ inputText }),

  refreshPorts: async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      set({ portsLoading: true });
    }

    try {
      const { ports, helperOnline, helperSetupRequired } = await listAvailablePorts();
      const { port: currentPort, connected } = get();
      const stillAvailable = ports.some((entry) => entry.id === currentPort);

      set({
        availablePorts: ports,
        helperOnline,
        helperSetupRequired,
        port: connected || stillAvailable ? currentPort : (ports[0]?.id ?? ''),
        portsLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to refresh COM ports.';
      set({ portsLoading: false });
      if (!options?.silent) {
        useConsoleStore.getState().addMessage('error', message);
      }
    }
  },

  refreshHelperStatus: async () => {
    const health = await fetchHelperHealth();
    const connection = resolveHelperConnectionState(health);
    if (connection === 'ready' || connection === 'setup_required') {
      markHelperInstalledOnThisDevice();
    }
    const helperOnline = connection === 'ready';
    const helperSetupRequired = connection === 'setup_required';
    const { helperOnline: wasOnline, helperSetupRequired: wasSetupRequired, connected } = get();

    if (helperOnline === wasOnline && helperSetupRequired === wasSetupRequired) {
      return;
    }

    set({ helperOnline, helperSetupRequired });

    if (!connected) {
      await get().refreshPorts({ silent: true });
    }
  },

  addWebSerialDevice: async () => {
    if (!get().webSerialSupported) {
      useConsoleStore.getState().addMessage(
        'error',
        'Web Serial is not supported in this browser. Use Chrome or Edge.',
      );
      return;
    }

    try {
      const device = await addAuthorizedWebSerialDevice();
      const { ports, helperOnline, helperSetupRequired } = await listAvailablePorts();
      set({
        availablePorts: ports,
        helperOnline,
        helperSetupRequired,
        port: device.portId,
      });
      useConsoleStore.getState().addMessage('success', `Added ${device.label}.`);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotFoundError') {
        return;
      }
      const message = error instanceof Error ? error.message : 'Failed to add serial device.';
      useConsoleStore.getState().addMessage('error', message);
    }
  },

  connect: async () => {
    const state = get();
    if (state.connected || state.connecting) {
      return;
    }

    if (!state.webSerialSupported) {
      useConsoleStore.getState().addMessage(
        'error',
        'Web Serial is not supported in this browser. Use Chrome or Edge.',
      );
      return;
    }

    set({ connecting: true });
    try {
      const connectPortId =
        state.port.startsWith('webserial-') ? state.port : state.port || null;

      activeRxLineBuffer = createSerialLineBuffer((line) => {
        set((current) => ({ logs: appendLog(current.logs, 'rx', line) }));
      });

      const result = await connectSerial(connectPortId, state.baudRate, (text) => {
        activeRxLineBuffer?.push(text);
      });

      const { ports, helperOnline, helperSetupRequired } = await listAvailablePorts();
      const keepComPortSelection = Boolean(
        resolveUploadPortPath(state.port, state.availablePorts),
      );

      set({
        connected: true,
        connecting: false,
        pausedForUpload: false,
        port: keepComPortSelection ? state.port : result.portId,
        availablePorts: ports,
        helperOnline,
        helperSetupRequired,
      });
      useConsoleStore.getState().addMessage('success', `Connected to ${result.label}.`);
    } catch (error) {
      activeRxLineBuffer?.clear();
      activeRxLineBuffer = null;
      const message =
        error instanceof DOMException && error.name === 'NotFoundError'
          ? 'No serial device was selected.'
          : error instanceof Error
            ? error.message
            : 'Failed to connect to the board.';
      set({ connecting: false });
      useConsoleStore.getState().addMessage('error', message);
    }
  },

  disconnect: async (options) => {
    if (!get().connected && !get().connecting) {
      return;
    }

    // Update UI immediately when the cable is unplugged or Disconnect is clicked.
    set({
      connected: false,
      connecting: false,
      pausedForUpload: options?.forUpload === true,
    });

    try {
      flushRxLineBuffer();
      await disconnectSerial();
    } catch {
      // Ignore close errors when the device is already unplugged.
    }

    if (options?.forUpload) {
      return;
    }

    set({ pausedForUpload: false });

    useConsoleStore.getState().addMessage(
      options?.unexpected ? 'warning' : 'info',
      'Board disconnected.',
    );

    void get().refreshPorts();
  },

  send: async () => {
    const { inputText, connected, lineEnding } = get();
    if (!connected || !inputText) {
      return;
    }

    const payload = inputText + lineEndingSuffix(lineEnding);
    try {
      await writeSerial(payload);
      set({
        inputText: '',
        logs: appendLog(get().logs, 'tx', payload),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send serial data.';
      useConsoleStore.getState().addMessage('error', message);
    }
  },

  clear: () => set({ logs: [] }),
}));
